import { useEffect, useRef, useState } from 'react';
import LightweightChart from '../components/LightweightChart';
import './ChartPage.css';
import { getLawById, laws } from '../data/laws';
import { useAppliedLaw } from '../context/AppliedLawContext';
import ChartErrorBoundary from '../components/ChartErrorBoundary';
import { keys } from '../utils/storage';
import MarketPanel from '../components/MarketPanel';
import { useMarketData } from '../context/MarketDataContext';
import { validateLawRenderable } from '../lib/lawEngine/validation';
import { buildLawDrawPlan } from '../lib/indicator/model';
import lawIndicatorMap from '../data/lawIndicatorMap.json';

const MAX_VISIBLE_LAWS = 8;
const PREVIEW_INTERVAL_MS = 280;

const ChartPage = () => {
  const {
    appliedLawId,
    appliedLawIds,
    hiddenLawIds,
    applyLaw,
    removeLaw,
    toggleLaw,
    clearAll,
    setActiveLaw,
    tutorialActive,
    tutorialLawId,
    tutorialStepIndex,
    tutorialStepCompleted,
    tutorialError,
    startTutorial,
    endTutorial,
    clearTutorialError,
    nextTutorialStep,
    previousTutorialStep
  } = useAppliedLaw();
  const [showEquilibrium, setShowEquilibrium] = useState(true);
  const [showKeyLevels, setShowKeyLevels] = useState(false);
  const [showZones, setShowZones] = useState(false);
  const [selectedLawId, setSelectedLawId] = useState(laws[0]?.id || '');
  const [validationReport, setValidationReport] = useState([]);
  const [overlayStats, setOverlayStats] = useState([]);
  const overlayStatsRef = useRef([]);
  const cycleTimerRef = useRef(null);
  const lastStatsAtRef = useRef(0);
  const { bars, latestBar, instrumentId, timeframeId } = useMarketData();
  const appliedLaw = appliedLawId ? getLawById(appliedLawId) : null;
  const appliedLawColor = appliedLaw?.color ?? getCategoryColor(appliedLaw?.category);
  const needsInputs = Boolean(appliedLaw?.chartRecipe?.inputs?.length);
  const isTutorialActive = tutorialActive && tutorialLawId === appliedLawId;
  const tutorialStep = isTutorialActive
    ? appliedLaw?.tutorialSteps?.[tutorialStepIndex]
    : null;
  const stepRequiresInput = Boolean(tutorialStep?.assigns);
  const isStepComplete = Boolean(tutorialStepCompleted[tutorialStepIndex]);
  const tutorialStepsCount = appliedLaw?.tutorialSteps?.length ?? 0;
  const isLastStep = Boolean(tutorialStepsCount && tutorialStepIndex === tutorialStepsCount - 1);
  const isChartDisabled = localStorage.getItem(keys.disableChart) === '1';

  const quickLaws = [
    { id: 'LAW_001', name: 'لا شك ليست حتمية', color: '#6366f1' },
    { id: 'LAW_002', name: 'قاعدة الانضباط', color: '#10b981' },
    { id: 'LAW_003', name: 'الوقف وعكس الوقف', color: '#f59e0b' },
    { id: 'LAW_004', name: 'الخادش', color: '#8b5cf6' },
    { id: 'LAW_005', name: 'اتزانات الفوضى', color: '#ef4444' }
  ];

  const visibleLawIds = appliedLawIds.filter((id) => !hiddenLawIds.includes(id));
  const visibleLawIdsLimited = visibleLawIds.slice(0, MAX_VISIBLE_LAWS);
  const autoHiddenCount = Math.max(0, visibleLawIds.length - visibleLawIdsLimited.length);

  const appliedLaws = visibleLawIdsLimited
    .map((id) => getLawById(id))
    .filter(Boolean);

  const handleApplyLaw = (law, mode = 'add', opts = {}) => {
    const skipTutorial = opts.skipTutorial === true;
    applyLaw(law.id, mode);
    setActiveLaw(law.id);
    clearTutorialError();
    if (skipTutorial) {
      endTutorial();
      return;
    }
    if (law?.chartRecipe?.inputs?.length) {
      startTutorial(law.id);
    } else {
      endTutorial();
    }
  };

  const stopPreview = () => {
    if (cycleTimerRef.current) {
      clearInterval(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
  };

  const cycleAllLaws = () => {
    stopPreview();
    let index = 0;
    cycleTimerRef.current = setInterval(() => {
      const law = laws[index];
      if (!law) {
        stopPreview();
        return;
      }
      handleApplyLaw(law, 'replace', { skipTutorial: true });
      index += 1;
    }, PREVIEW_INTERVAL_MS);
  };

  const validateAllLaws = async () => {
    const results = laws.map((law) => {
      const plan = buildLawDrawPlan({ law, bars, mapping: lawIndicatorMap });
      return validateLawRenderable({
        law,
        renderedMarkers: plan.markersCount,
        renderedLines: plan.linesCount,
        renderedBoxes: plan.boxesCount,
        renderedLawSpecific: plan.lawSpecificCount,
        fallbackVisible: plan.unknownMapping,
      });
    });
    setValidationReport(results);
    console.table(results);
  };

  useEffect(() => () => stopPreview(), []);

  const hasRecipeOverlays = Boolean(appliedLaw?.chartRecipe?.overlays?.length);
  const showConditions = Boolean(appliedLaw && !needsInputs && !hasRecipeOverlays);

  return (
    <div className="chart-page">
      <div className="chart-header">
        <h1 className="page-title">الشارت التفاعلي</h1>
        <p className="page-subtitle">
          حمّل بياناتك الخاصة أو استخدم البيانات التجريبية، ثم طبّق القوانين مباشرة على الشارت
        </p>
      </div>

      <div className="chart-layout">
        <div className="chart-main">
          <div className="overlay-controls">
            <div className="overlay-header">
              <h3>🎯 أدوات الإيضاح البصري</h3>
              <p>خطوط إرشادية لتوضيح نسب النظام على بياناتك.</p>
            </div>
            <div className="overlay-options">
              <label className="overlay-option">
                <input
                  type="checkbox"
                  checked={showEquilibrium}
                  onChange={(event) => setShowEquilibrium(event.target.checked)}
                />
                عرض مستوى الاتزان 0.236
              </label>
              <label className="overlay-option">
                <input
                  type="checkbox"
                  checked={showKeyLevels}
                  onChange={(event) => setShowKeyLevels(event.target.checked)}
                />
                عرض المستويات الرئيسية (0.382 / 0.5 / 0.618 / 0.786)
              </label>
              <label className="overlay-option">
                <input
                  type="checkbox"
                  checked={showZones}
                  onChange={(event) => setShowZones(event.target.checked)}
                />
                إبراز حدود المنطقة الآمنة والمثلى
              </label>
            </div>
            <p className="overlay-note">
              هذه الخطوط إرشادية للتعلم وليست توصية تداول مباشرة.
            </p>
          </div>
          <MarketPanel />
          <div className="quick-laws" style={{ marginBottom: '1rem' }}>
            <h3 className="panel-title">📚 جميع القوانين</h3>
            <div className="laws-list">
              <label className="overlay-option" style={{ width: '100%' }}>
                <select
                  value={selectedLawId}
                  onChange={(event) => setSelectedLawId(event.target.value)}
                  style={{ width: '100%' }}
                >
                  {laws.map((law) => (
                    <option key={law.id} value={law.id}>
                      {law.id} — {law.title}
                    </option>
                  ))}
                </select>
              </label>
              <button
                className="law-button"
                style={{ '--law-color': getCategoryColor(getLawById(selectedLawId)?.category) }}
                onClick={() => {
                  const law = getLawById(selectedLawId);
                  if (law) {
                    handleApplyLaw(law, 'add');
                  }
                }}
              >
                إضافة القانون المحدد
              </button>
              <button className="law-button" style={{ '--law-color': '#0ea5e9' }} onClick={cycleAllLaws}>
                معاينة كل القوانين (قانون واحد)
              </button>
              <button className="law-button" style={{ '--law-color': '#64748b' }} onClick={stopPreview}>
                إيقاف المعاينة
              </button>
              <button data-testid="validate-all-laws" className="law-button" style={{ '--law-color': '#22c55e' }} onClick={validateAllLaws}>
                Validate all laws
              </button>
            </div>
            {autoHiddenCount > 0 && (
              <div className="chart-error" role="status" style={{ marginTop: '0.5rem' }}>
                تم إخفاء بعض القوانين لتجنب تعليق الشارت. يمكنك إظهارها تدريجياً. ({autoHiddenCount})
              </div>
            )}
            {validationReport.length > 0 && (
              <ul data-testid="law-validation-results" className="law-conditions">
                {validationReport.map((item) => (
                  <li data-testid={`law-result-${item.id}`} key={item.id}>{item.id}: {item.status === 'pass' ? '✅' : '❌'}</li>
                ))}
              </ul>
            )}
          </div>
          {isChartDisabled ? (
            <div className="chart-error" role="alert">
              تم تعطيل الشارت مؤقتًا. أزل المفتاح من التخزين المحلي لإعادة التفعيل.
            </div>
          ) : (
            <ChartErrorBoundary
              symbol={instrumentId}
              timeframe={timeframeId}
              barsCount={bars.length}
              lastBarTime={bars[bars.length - 1]?.time}
            >
              <LightweightChart
                height={600}
                showControls={true}
                advancedControls={true}
                showEquilibrium={showEquilibrium}
                showKeyLevels={showKeyLevels}
                showZones={showZones}
                appliedLaw={appliedLaw}
                appliedLaws={appliedLaws}
                onOverlayStatsChange={(stats) => {
                  overlayStatsRef.current = stats;
                  const now = Date.now();
                  if (now - lastStatsAtRef.current < 250) return;
                  lastStatsAtRef.current = now;
                  setOverlayStats(stats);
                }}
                externalBars={bars}
                latestBar={latestBar}
              />
            </ChartErrorBoundary>
          )}
        </div>

        <div className="chart-sidebar">
          <div className="law-panel">
            <h3 className="panel-title">📍 القانون المطبق</h3>
            {appliedLawIds.length > 0 && (
              <div className="laws-list" style={{ marginBottom: '0.75rem' }}>
                {appliedLawIds.map((lawId) => {
                  const law = getLawById(lawId);
                  if (!law) return null;
                  return (
                    <div key={lawId} style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="law-button" style={{ '--law-color': law.color || getCategoryColor(law.category), flex: 1 }} onClick={() => setActiveLaw(lawId)}>
                        {lawId}
                      </button>
                      <button className="btn-remove" onClick={() => toggleLaw(lawId)}>{hiddenLawIds.includes(lawId) ? 'إظهار' : 'إخفاء'}</button>
                      <button className="btn-remove" onClick={() => removeLaw(lawId)}>حذف</button>
                    </div>
                  );
                })}
                <button className="btn-remove" onClick={clearAll}>مسح كل القوانين</button>
              </div>
            )}
            {appliedLaw ? (
              <div className="applied-law" style={{ borderColor: appliedLawColor }}>
                <div className="law-badge" style={{ background: appliedLawColor }}>
                  {appliedLaw.id}
                </div>
                <h4 className="law-name">{appliedLaw.title}</h4>
                <p className="law-explanation">
                  {needsInputs
                    ? (tutorialStep?.text || 'يتطلب هذا القانون تحديد نقاط على الشارت قبل التطبيق.')
                    : (appliedLaw.summary || 'تم تطبيق القانون على الشارت.')}
                </p>
                {needsInputs && isTutorialActive && (
                  <div className="tutorial-controls">
                    {tutorialError && (
                      <p className="tutorial-error">{tutorialError}</p>
                    )}
                    <div className="tutorial-buttons">
                      {tutorialStepIndex > 0 && (
                        <button
                          className="btn-remove"
                          onClick={previousTutorialStep}
                        >
                          السابق
                        </button>
                      )}
                      {isLastStep ? (
                        <button
                          className="btn-remove"
                          onClick={endTutorial}
                          disabled={stepRequiresInput && !isStepComplete}
                        >
                          إتمام
                        </button>
                      ) : (
                        <button
                          className="btn-remove"
                          onClick={nextTutorialStep}
                          disabled={stepRequiresInput && !isStepComplete}
                        >
                          التالي
                        </button>
                      )}
                    </div>
                  </div>
                )}
                <button 
                  className="btn-remove"
                  onClick={() => {
                    removeLaw(appliedLaw.id);
                    clearTutorialError();
                    endTutorial();
                  }}
                >
                  إزالة التطبيق
                </button>
                {showConditions && (
                  <ul data-testid="law-validation-results" className="law-conditions">
                    {appliedLaw.conditions.map((condition, index) => (
                      <li key={index}>✅ {condition}</li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="no-law">
                <p>لم يتم تطبيق أي قانون بعد</p>
                <p className="hint">اختر قانوناً من القائمة أدناه</p>
              </div>
            )}
          </div>

          <div className="quick-laws">
            <h3 className="panel-title">⚡ تطبيق سريع</h3>
            <div className="laws-list">
              {quickLaws.map((law) => (
                <button
                  key={law.id}
                  className="law-button"
                  style={{ '--law-color': law.color }}
                  onClick={() => handleApplyLaw(law)}
                >
                  <span className="law-id">{law.id}</span>
                  <span className="law-name-short">{law.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="chart-info">
            <h3 className="panel-title">ℹ️ معلومات</h3>
            <ul className="info-list">
              <li>استخدم CSV بتنسيق: Date,Open,High,Low,Close,Volume</li>
              <li>التاريخ بصيغة: YYYY-MM-DD</li>
              <li>الأسعار والحجم أرقام عشرية</li>
              <li>يمكنك تطبيق عدة قوانين معاً</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

function getCategoryColor(category) {
  const colors = {
    'مدرسة': '#6366f1',
    'مؤشر': '#10b981',
    'كوني': '#f97316',
    'ذكاء اصطناعي': '#22c55e',
    'مشترك': '#a855f7',
    'تطبيق': '#f59e0b'
  };

  return colors[category] || '#6366f1';
}

export default ChartPage;
